import { clsx } from 'clsx';
import type {
  PdoVersionStatus,
  GardenRunStatus,
  GardenRunType,
} from '@/services/api';

const VERSION_STATUS: Record<PdoVersionStatus, { label: string; className: string }> = {
  current: {
    label: 'Current',
    className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  draft: {
    label: 'Draft',
    className: 'bg-slate-100 text-slate-600 border-slate-200',
  },
  validated: {
    label: 'Validated',
    className: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  superseded: {
    label: 'Superseded',
    className: 'bg-slate-50 text-slate-500 border-slate-200',
  },
  archived: {
    label: 'Archived',
    className: 'bg-slate-50 text-slate-400 border-slate-100',
  },
};

export function VersionStatusBadge({ status }: { status: PdoVersionStatus }) {
  const cfg = VERSION_STATUS[status] ?? VERSION_STATUS.draft;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider',
        cfg.className,
      )}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {cfg.label}
    </span>
  );
}

const RUN_STATUS: Record<GardenRunStatus, { label: string; className: string }> = {
  pending: {
    label: 'Pending',
    className: 'bg-slate-100 text-slate-600 border-slate-200',
  },
  running: {
    label: 'Running',
    className: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  completed: {
    label: 'Completed',
    className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  published: {
    label: 'Published',
    className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-100 text-red-700 border-red-200',
  },
  pending_review: {
    label: 'Pending Review',
    className: 'bg-purple-100 text-purple-700 border-purple-200',
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-slate-100 text-slate-500 border-slate-200',
  },
};

export function RunStatusBadge({ status }: { status: GardenRunStatus }) {
  const cfg = RUN_STATUS[status] ?? RUN_STATUS.pending;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider',
        cfg.className,
      )}
    >
      <span
        className={clsx(
          'w-1.5 h-1.5 rounded-full bg-current opacity-70',
          status === 'running' && 'animate-pulse',
        )}
      />
      {cfg.label}
    </span>
  );
}

const RUN_TYPE: Record<GardenRunType, { label: string; className: string }> = {
  creative_batch: {
    label: 'Creative Batch',
    className: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  performance_refresh: {
    label: 'Performance Refresh',
    className: 'bg-teal-100 text-teal-700 border-teal-200',
  },
  compass_directed: {
    label: 'Compass Directed',
    className: 'bg-purple-100 text-purple-700 border-purple-200',
  },
  scheduled_rebuild: {
    label: 'Scheduled Rebuild',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  },
  creative_memory_cycle: {
    label: 'Memory Cycle',
    className: 'bg-violet-100 text-violet-700 border-violet-200',
  },
  strategic_rebuild: {
    label: 'Strategic Rebuild',
    className: 'bg-orange-100 text-orange-700 border-orange-200',
  },
  creative_review_backfill: {
    label: 'Creative Review Backfill',
    className: 'bg-teal-100 text-teal-700 border-teal-200',
  },
  creative_review_refresh: {
    label: 'Creative Review Refresh',
    className: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  },
  creative_review_seed: {
    label: 'Creative Review Seed',
    className: 'bg-green-100 text-green-700 border-green-200',
  },
  strategy_docket_refresh: {
    label: 'Strategy Docket',
    className: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  },
};

export function RunTypeBadge({ runType }: { runType: GardenRunType }) {
  const cfg = RUN_TYPE[runType] ?? {
    label: runType,
    className: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-medium',
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  );
}

const MATURITY: Record<string, { label: string; className: string }> = {
  candidate: {
    label: 'Candidate',
    className: 'bg-slate-100 text-slate-600 border-slate-200',
  },
  active: {
    label: 'Active',
    className: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  established: {
    label: 'Established',
    className: 'bg-teal-100 text-teal-700 border-teal-200',
  },
  hardened: {
    label: 'Hardened',
    className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
};

export function MaturityBadge({ stage }: { stage: string }) {
  const cfg = MATURITY[stage] ?? {
    label: stage,
    className: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span
      className={clsx(
        'inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium',
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  );
}

const STAGE_STATUS_ICONS: Record<string, { symbol: string; className: string }> = {
  pass: { symbol: '✓', className: 'text-emerald-600' },
  skip: { symbol: '–', className: 'text-slate-400' },
  fail: { symbol: '✗', className: 'text-red-600' },
  warn: { symbol: '⚠', className: 'text-amber-600' },
};

export function StageStatusIcon({ status }: { status: string }) {
  const cfg = STAGE_STATUS_ICONS[status] ?? STAGE_STATUS_ICONS.skip;
  return (
    <span
      className={clsx('inline-block w-4 text-center font-bold', cfg.className)}
      title={status}
    >
      {cfg.symbol}
    </span>
  );
}
